import { EmptyState, ErrorState, LoadingState, formatLabel } from '../components/FdPrimitives.tsx';
import { useFetch } from '../hooks/useFetch.ts';

type KpiSnapshot = {
  value: number | null;
  eligible: number;
  note?: string;
};

export function KpisPage() {
  const state = useFetch<Record<string, unknown>>('/kpis');

  if (state.status === 'loading') return <LoadingState label="Loading KPIs" />;
  if (state.status === 'error') return <ErrorState message={state.message} />;

  const stats = Object.entries(state.data).filter((entry): entry is [string, KpiSnapshot] => isKpiSnapshot(entry[1]));

  if (stats.length === 0 || stats.every(([, stat]) => stat.value === null && stat.eligible === 0)) {
    return <EmptyState icon="monitoring" title="0 of 0 — waiting on data" message="KPI tiles will populate once pilot volume is eligible." />;
  }

  return (
    <div className="page-stack">
      <div className="page-head">
        <div>
          <h2>KPIs</h2>
          <p>Current tenant performance snapshot.</p>
        </div>
      </div>
      <div className="statgrid">
        {stats.map(([key, stat], index) => (
          <div className={statClass(index)} key={key}>
            <span className="ac" />
            <div className="l">{formatLabel(key)}</div>
            <div className="n">{formatKpiValue(key, stat.value)}</div>
            <span className="dl">{stat.note || `${stat.eligible.toLocaleString()} eligible`}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function isKpiSnapshot(value: unknown): value is KpiSnapshot {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<KpiSnapshot>;
  return 'value' in candidate && typeof candidate.eligible === 'number';
}

function statClass(index: number) {
  if (index % 3 === 1) return 'stat gold';
  if (index % 3 === 2) return 'stat teal';
  return 'stat';
}

function formatKpiValue(key: string, value: number | null) {
  if (value === null) return 'Waiting';
  if (key.includes('gmv')) return `$${Math.round(value).toLocaleString()}`;
  if (key.includes('rate') || key.includes('share')) return `${value.toFixed(2)}%`;
  if (key.includes('minutes')) return `${value.toFixed(1)}m`;
  return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(2);
}
